import React, { useState, useRef, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XIcon, MailIcon, SmartphoneIcon, LinkIcon, CheckIcon, UserIcon, EyeOffIcon, SendIcon, DollarSignIcon, QrCodeIcon, DownloadIcon, CalendarIcon, PercentIcon, BuildingIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Recipient, Supplier, Installment } from './InstallmentPayment';
import { QRCodeSVG } from 'qrcode.react';
interface ShareLinkModalProps {
  onClose: () => void;
  splitType?: 'dates' | 'recipients' | 'commission' | null;
  recipients?: Recipient[];
  installments?: Installment[];
  suppliers?: Supplier[];
  totalAmount?: string;
  commissionType?: 'percentage' | 'fixed';
}
export function ShareLinkModal({
  onClose,
  splitType = null,
  recipients = [],
  installments = [],
  suppliers = [],
  totalAmount = '0',
  commissionType = 'percentage'
}: ShareLinkModalProps) {
  const {
    t
  } = useTranslation('payments');
  const [step, setStep] = useState<'preview' | 'share'>(() => {
    // Recipients mode goes straight to share (multi-recipient view)
    // No splitType (standard) also goes straight to share
    if (splitType === 'recipients' || !splitType) return 'share';
    // Schedule and commission start with preview
    return 'preview';
  });
  const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | 'copy' | 'qrcode' | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  // For multiple recipients
  const [recipientContactMethods, setRecipientContactMethods] = useState<Record<number, 'email' | 'sms' | 'both'>>({});
  const [copiedRecipientId, setCopiedRecipientId] = useState<number | null>(null);
  const [recipientsViewTotalSettings, setRecipientsViewTotalSettings] = useState<Record<number, boolean>>(() => {
    const initialSettings: Record<number, boolean> = {};
    recipients.forEach(r => {
      initialSettings[r.id] = false;
    });
    return initialSettings;
  });
  const hasMultipleRecipients = splitType === 'recipients' && recipients.length > 0;
  const handleCopyLink = (recipientId?: number) => {
    const linkToCopy = recipientId ? `https://payment.example.com/pay/${recipientId}-abc123${recipientsViewTotalSettings[recipientId] ? '?showTotal=true' : ''}` : 'https://payment.example.com/pay/abc123';
    navigator.clipboard.writeText(linkToCopy);
    if (recipientId) {
      setCopiedRecipientId(recipientId);
      setTimeout(() => setCopiedRecipientId(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'payment-qrcode.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setQrDownloaded(true);
    setTimeout(() => setQrDownloaded(false), 2000);
  };
  const handleSend = () => {
    onClose();
  };
  const getContactMethod = (recipient: Recipient): 'email' | 'sms' | 'both' => {
    if (recipientContactMethods[recipient.id]) {
      return recipientContactMethods[recipient.id];
    }
    if (recipient.email && recipient.phone) return 'both';
    if (recipient.email) return 'email';
    if (recipient.phone) return 'sms';
    return 'email';
  };
  const setContactMethod = (recipientId: number, method: 'email' | 'sms' | 'both') => {
    setRecipientContactMethods(prev => ({
      ...prev,
      [recipientId]: method
    }));
  };
  const toggleRecipientTotalView = (recipientId: number) => {
    setRecipientsViewTotalSettings(prev => ({
      ...prev,
      [recipientId]: !prev[recipientId]
    }));
  };
  // Calculate commission values
  const totalAmountNum = parseFloat(totalAmount) || 0;
  const totalCommission = suppliers.reduce((sum, s) => {
    if (commissionType === 'percentage') {
      return sum + totalAmountNum * (parseFloat(s.percentage || '0') / 100);
    }
    return sum + (parseFloat(s.amount) || 0);
  }, 0);
  const businessShare = totalAmountNum - totalCommission;
  const getModalTitle = () => {
    if (step === 'preview') {
      if (splitType === 'dates') return 'Payment schedule preview';
      if (splitType === 'commission') return 'Commission split preview';
    }
    if (hasMultipleRecipients) return 'Share payment link';
    return 'Share payment link';
  };
  // ─── SCHEDULE PREVIEW ───
  const renderSchedulePreview = () => <div className="space-y-4" data-id="element-7875">
      <p className="text-sm text-gray-600" data-id="element-7876">
        {t('components.renderSchedulePreview.review_the_payment_schedule_before_creating')}
      </p>

      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-7877">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-7878">
          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7879">
            <div className="col-span-1" data-id="element-7880">#</div>
            <div className="col-span-3" data-id="element-7881">
              {t('components.BulkPaymentPreview.amount')}
            </div>
            <div className="col-span-3" data-id="element-7882">
              {t('components.InstallmentPayment.due_date')}
            </div>
            <div className="col-span-3" data-id="element-7883">
              {t('components.InstallmentPayment.send_link_on')}
            </div>
            <div className="col-span-2" data-id="element-7884">
              {t('components.BulkPaymentList.status')}
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-7885">
          {installments.map((inst, index) => <div key={inst.id} className="px-4 py-3.5" data-id="element-7886">
              <div className="grid grid-cols-12 gap-3 items-center" data-id="element-7887">
                <div className="col-span-1" data-id="element-7888">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#E2231A]/10 text-[#9F140F] rounded-full font-bold text-xs" data-id="element-7889">
                    {index + 1}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-7890">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-7891">
                    {formatCurrencyAmount(parseFloat(inst.amount || '0'), {
                  fractionDigits: 2
                })}
                  </span>
                  {inst.description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-7892">
                      {inst.description}
                    </p>}
                </div>
                <div className="col-span-3" data-id="element-7893">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-7894">
                    <CalendarIcon size={13} className="mr-1.5 text-gray-400" data-id="element-7895" />
                    {inst.dueDate || 'Not set'}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-7896">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-7897">
                    <SendIcon size={13} className="mr-1.5 text-gray-400" data-id="element-7898" />
                    {inst.sendLinkDate || 'Auto'}
                  </div>
                </div>
                <div className="col-span-2" data-id="element-7899">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-7900">
                    {t('components.renderSchedulePreview.pending')}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-7901">
          <div className="flex justify-between items-center" data-id="element-7902">
            <span className="text-sm font-medium text-gray-600" data-id="element-7903">
              {t('components.renderSchedulePreview.total')}
            </span>
            <span className="text-sm font-bold text-gray-900" data-id="element-7904">
              {formatCurrencyAmount(installments.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0), {
              fractionDigits: 2
            })}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[#E2231A]/5 border border-[#E2231A]/20 rounded-xl p-4" data-id="element-7905">
        <div className="flex items-start gap-3" data-id="element-7906">
          <CalendarIcon size={18} className="text-[#9F140F] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-7907" />

          <div data-id="element-7908">
            <p className="text-sm font-medium text-gray-900" data-id="element-7909">
              {t('components.renderSchedulePreview.automatic_link_delivery')}
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-7910">
              {t('components.renderSchedulePreview.payment_links_will_be_sent_automatically')}
            </p>
          </div>
        </div>
      </div>
    </div>;
  // ─── COMMISSION PREVIEW ───
  const renderCommissionPreview = () => <div className="space-y-4" data-id="element-7911">
      <p className="text-sm text-gray-600" data-id="element-7912">
        {t('components.renderCommissionPreview.review_the_commission_breakdown_before_creating')}
      </p>

      {/* Total amount card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5" data-id="element-7913">
        <div className="flex items-center justify-between mb-4" data-id="element-7914">
          <span className="text-sm font-medium text-gray-500" data-id="element-7915">
            {t('components.InstallmentPayment.total_payment_amount')}
          </span>
          <span className="text-xl font-bold text-gray-900" data-id="element-7916">
            {formatCurrencyAmount(totalAmountNum, {
            fractionDigits: 2
          })}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-7917">
          <div className="h-full rounded-full bg-gradient-to-r from-[#E2231A] to-[#E2231A]/70" style={{
          width: totalAmountNum > 0 ? `${Math.min(totalCommission / totalAmountNum * 100, 100)}%` : '0%'
        }} data-id="element-7918" />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500" data-id="element-7919">
          <span className="flex items-center gap-1" data-id="element-7920">
            <span className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-7921" />
            {t('components.renderCommissionPreview.commission_usd')}{' '}
            {formatCurrencyAmount(totalCommission, {
            fractionDigits: 2
          })}
          </span>
          <span className="flex items-center gap-1" data-id="element-7922">
            <span className="w-2 h-2 rounded-full bg-gray-300" data-id="element-7923" />
            {t('components.renderCommissionPreview.your_share_usd')}{' '}
            {formatCurrencyAmount(businessShare, {
            fractionDigits: 2
          })}
          </span>
        </div>
      </div>

      {/* Supplier breakdown */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-7924">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-7925">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7926">
            {t('components.renderCommissionPreview.supplier_commission_breakdown')}
          </h4>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-7927">
          {suppliers.map((supplier, index) => {
          const supplierAmount = commissionType === 'percentage' ? totalAmountNum * (parseFloat(supplier.percentage || '0') / 100) : parseFloat(supplier.amount) || 0;
          const supplierPercentage = commissionType === 'percentage' ? parseFloat(supplier.percentage || '0') : totalAmountNum > 0 ? supplierAmount / totalAmountNum * 100 : 0;
          return <div key={supplier.id} className="px-4 py-4" data-id="element-7928">
                <div className="flex items-center justify-between" data-id="element-7929">
                  <div className="flex items-center gap-3" data-id="element-7930">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E2231A]/10 text-[#9F140F] rounded-full font-bold text-xs flex-shrink-0" data-id="element-7931">
                      {index + 1}
                    </div>
                    <div data-id="element-7932">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-7933">
                        {supplier.name || `Supplier ${index + 1}`}
                      </p>
                      {supplier.email && <p className="text-xs text-gray-500" data-id="element-7934">
                          {supplier.email}
                        </p>}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-7935">
                    <p className="text-sm font-bold text-gray-900" data-id="element-7936">
                      {formatCurrencyAmount(supplierAmount, {
                    fractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-[#9F140F] font-medium" data-id="element-7937">
                      {supplierPercentage.toFixed(1)}
                      {t('components.renderCommissionPreview.of_total')}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Your business share */}
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-200" data-id="element-7938">
          <div className="flex items-center justify-between" data-id="element-7939">
            <div className="flex items-center gap-3" data-id="element-7940">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full flex-shrink-0" data-id="element-7941">
                <BuildingIcon size={14} data-id="element-7942" />
              </div>
              <div data-id="element-7943">
                <p className="text-sm font-semibold text-gray-900" data-id="element-7944">
                  {t('components.renderCommissionPreview.your_business')}
                </p>
                <p className="text-xs text-gray-500" data-id="element-7945">
                  {t('components.renderCommissionPreview.remaining_after_commission')}
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-7946">
              <p className="text-sm font-bold text-gray-900" data-id="element-7947">
                {formatCurrencyAmount(businessShare, {
                fractionDigits: 2
              })}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-7948">
                {totalAmountNum > 0 ? (businessShare / totalAmountNum * 100).toFixed(1) : '0.0'}
                {t('components.renderCommissionPreview.of_total')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  // ─── MULTI-RECIPIENT SHARE VIEW ───
  const renderMultiRecipientShare = () => <>
      <div className="flex justify-between items-center mb-4" data-id="element-7949">
        <p className="text-gray-600 font-medium" data-id="element-7950">
          {t('components.renderMultiRecipientShare.send_unique_payment_links_to_each')}
        </p>
      </div>
      <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-id="element-7951">
        {recipients.map((recipient, index) => <div key={recipient.id} className="border border-gray-200 rounded-xl p-4" data-id="element-7952">
            <div className="flex items-center mb-3" data-id="element-7953">
              <div className="w-10 h-10 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-7954">
                <UserIcon size={20} className="text-[#9F140F]" aria-hidden="true" data-id="element-7955" />
              </div>
              <div className="ml-3" data-id="element-7956">
                <h4 className="font-medium text-gray-800" data-id="element-7957">
                  {t('components.renderMultiRecipientShare.recipient')}{' '}
                  {index + 1}
                </h4>
                <p className="text-sm text-gray-500" data-id="element-7958">
                  {recipient.email || recipient.phone}
                </p>
              </div>
            </div>

            {/* Toggle */}
            <div className="mb-3" data-id="element-7959">
              <label className="flex items-center cursor-pointer" data-id="element-7960">
                <input type="checkbox" className="sr-only" checked={recipientsViewTotalSettings[recipient.id] || false} onChange={() => toggleRecipientTotalView(recipient.id)} data-id="element-7961" />

                <div className={`w-10 h-5 rounded-full transition-colors ${recipientsViewTotalSettings[recipient.id] ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-7962">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${recipientsViewTotalSettings[recipient.id] ? 'translate-x-5' : ''}`} data-id="element-7963" />
                </div>
                <span className="ml-2 text-sm text-gray-600" data-id="element-7964">
                  {recipientsViewTotalSettings[recipient.id] ? <span className="flex items-center" data-id="element-7965">
                      <DollarSignIcon size={14} className="mr-1" data-id="element-7966" />
                      {t('components.renderMultiRecipientShare.allow_recipient_to_view_the_total')}
                    </span> : <span className="flex items-center" data-id="element-7967">
                      <EyeOffIcon size={14} className="mr-1" data-id="element-7968" />
                      {t('components.renderMultiRecipientShare.recipient_will_only_see_their_portion')}
                    </span>}
                </span>
              </label>
            </div>

            {/* Payment link */}
            <div className="mb-3" data-id="element-7969">
              <div className="flex" data-id="element-7970">
                <input type="text" className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg focus:ring-#E2231A/20 focus:border-#E2231A bg-gray-50 text-sm" value={`https://payment.example.com/pay/${recipient.id}-abc123${recipientsViewTotalSettings[recipient.id] ? '?showTotal=true' : ''}`} readOnly data-id="element-7971" />

                <button className="px-3 py-2 bg-[#E2231A] text-white rounded-r-lg hover:bg-[#EB4A42] transition-colors flex items-center text-sm font-medium" onClick={() => handleCopyLink(recipient.id)} data-id="element-7972">
                  {copiedRecipientId === recipient.id ? <>
                      <CheckIcon size={14} className="mr-1" data-id="element-7973" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={14} className="mr-1" data-id="element-7974" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>

            {/* Contact options */}
            {(recipient.email || recipient.phone) && <div className="mt-3" data-id="element-7975">
                <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-7976">
                  {t('components.renderMultiRecipientShare.send_payment_link_via')}
                </h5>
                <div className="flex space-x-2" data-id="element-7977">
                  {recipient.email && recipient.phone && <>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'email' ? 'bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'email')} data-id="element-7978">
                        <MailIcon size={14} className="mr-1.5" data-id="element-7979" />
                        {t('components.RecurringPayment.email')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'sms' ? 'bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'sms')} data-id="element-7980">
                        <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7981" />
                        {t('components.renderMultiRecipientShare.sms')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'both' ? 'bg-[#E2231A] text-white border border-[#E2231A]' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'both')} data-id="element-7982">
                        {t('components.renderMultiRecipientShare.both')}
                      </button>
                    </>}
                  {recipient.email && !recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30 cursor-default" data-id="element-7983">
                      <MailIcon size={14} className="mr-1.5" data-id="element-7984" />
                      {t('components.renderMultiRecipientShare.email_only')}
                    </button>}
                  {!recipient.email && recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30 cursor-default" data-id="element-7985">
                      <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7986" />
                      {t('components.renderMultiRecipientShare.sms_only')}
                    </button>}
                </div>
              </div>}

            {/* Display contact info */}
            <div className="mt-3 text-sm space-y-1" data-id="element-7987">
              {(getContactMethod(recipient) === 'email' || getContactMethod(recipient) === 'both') && recipient.email && <div className="flex items-center text-gray-600" data-id="element-7988">
                    <MailIcon size={14} className="mr-1.5" data-id="element-7989" />
                    {recipient.email}
                  </div>}
              {(getContactMethod(recipient) === 'sms' || getContactMethod(recipient) === 'both') && recipient.phone && <div className="flex items-center text-gray-600" data-id="element-7990">
                    <SmartphoneIcon size={14} className="mr-1.5" data-id="element-7991" />
                    {recipient.phone}
                  </div>}
            </div>
          </div>)}
      </div>
    </>;
  // ─── STANDARD SHARE VIEW (single link) ───
  const renderStandardShare = () => <>
      <p className="text-gray-600 mb-4" data-id="element-7992">
        {t('components.renderStandardShare.choose_how_you_want_to_share')}
      </p>
      <div className="space-y-3" data-id="element-7993">
        {/* Email option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'email' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('email')} data-id="element-7994">
          <div className="flex items-center" data-id="element-7995">
            <div className="w-10 h-10 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-7996">
              <MailIcon size={20} className="text-[#9F140F]" data-id="element-7997" />
            </div>
            <div className="ml-4 flex-1" data-id="element-7998">
              <h4 className="font-medium text-gray-800" data-id="element-7999">
                {t('components.renderStandardShare.send_via_email')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8000">
                {t('components.renderStandardShare.send_the_payment_link_to_their')}
              </p>
            </div>
          </div>
          {selectedOption === 'email' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8001">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8002">
                {t('components.InstallmentPayment.attrs.email_address')}
              </label>
              <input type="email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#E2231A/20 focus:border-#E2231A transition-colors" placeholder={t('components.renderStandardShare.attrs.nameexamplecom')} value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-8003" />
            </div>}
        </div>

        {/* SMS option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'sms' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('sms')} data-id="element-8004">
          <div className="flex items-center" data-id="element-8005">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-8006">
              <SmartphoneIcon size={20} className="text-green-600" data-id="element-8007" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8008">
              <h4 className="font-medium text-gray-800" data-id="element-8009">
                {t('components.renderStandardShare.send_via_text_message')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8010">
                {t('components.renderStandardShare.send_the_payment_link_to_their_2')}
              </p>
            </div>
          </div>
          {selectedOption === 'sms' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8011">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8012">
                {t('components.InstallmentPayment.attrs.phone_number')}
              </label>
              <input type="tel" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#E2231A/20 focus:border-#E2231A transition-colors" placeholder="+1 (555) 123-4567" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-8013" />
            </div>}
        </div>

        {/* QR Code option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'qrcode' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('qrcode')} data-id="element-8014">
          <div className="flex items-center" data-id="element-8015">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-8016">
              <QrCodeIcon size={20} className="text-amber-600" data-id="element-8017" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8018">
              <h4 className="font-medium text-gray-800" data-id="element-8019">
                {t('components.renderStandardShare.generate_qr_code')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8020">
                {t('components.renderStandardShare.create_a_scannable_qr_code_for')}
              </p>
            </div>
          </div>
          {selectedOption === 'qrcode' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8021">
              <div className="flex flex-col items-center" data-id="element-8022">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3" data-id="element-8023">
                  <QRCodeSVG value="https://payment.example.com/pay/abc123" size={200} level="H" includeMargin={true} data-id="element-8024" />
                </div>
                <button className="px-4 py-2 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors flex items-center font-medium text-sm" onClick={handleDownloadQR} data-id="element-8025">
                  {qrDownloaded ? <>
                      <CheckIcon size={16} className="mr-2" data-id="element-8026" />
                      {t('components.renderStandardShare.downloaded')}
                    </> : <>
                      <DownloadIcon size={16} className="mr-2" data-id="element-8027" />
                      {t('components.renderStandardShare.download_qr_code')}
                    </>}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-8028">
                  {t('components.renderStandardShare.customers_can_scan_this_qr_code')}
                </p>
              </div>
            </div>}
        </div>

        {/* Copy link option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'copy' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('copy')} data-id="element-8029">
          <div className="flex items-center" data-id="element-8030">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-8031">
              <LinkIcon size={20} className="text-purple-600" data-id="element-8032" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8033">
              <h4 className="font-medium text-gray-800" data-id="element-8034">
                {t('components.renderStandardShare.copy_link_to_share')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8035">
                {t('components.renderStandardShare.copy_the_payment_link_to_share')}
              </p>
            </div>
          </div>
          {selectedOption === 'copy' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8036">
              <div className="flex" data-id="element-8037">
                <input type="text" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-l-lg focus:ring-#E2231A/20 focus:border-#E2231A bg-gray-50 text-sm" value="https://payment.example.com/pay/abc123" readOnly data-id="element-8038" />

                <button className="px-4 py-2.5 bg-[#E2231A] text-white rounded-r-lg hover:bg-[#EB4A42] transition-colors flex items-center font-medium text-sm" onClick={() => handleCopyLink()} data-id="element-8039">
                  {copied ? <>
                      <CheckIcon size={16} className="mr-1" data-id="element-8040" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={16} className="mr-1" data-id="element-8041" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
  // ─── FOOTER BUTTONS ───
  const renderFooter = () => {
    if (step === 'preview') {
      return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-8042">
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-8043">
            {t('components.CardOnFilePayment.cancel')}
          </button>
          <button className="px-5 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors flex items-center text-sm font-medium" onClick={() => setStep('share')} data-id="element-8044">
            {t('components.renderFooter.continue_to_share')}
            <ArrowRightIcon size={16} className="ml-2" data-id="element-8045" />
          </button>
        </div>;
    }
    // Share step
    const showBackButton = splitType === 'dates' || splitType === 'commission';
    return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-8046">
        <div className="flex items-center gap-2" data-id="element-8047">
          {showBackButton && <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center" onClick={() => setStep('preview')} data-id="element-8048">
              <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-8049" />
              {t('components.BulkPaymentPreview.back')}
            </button>}
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-8050">
            {t('components.CardOnFilePayment.cancel')}
          </button>
        </div>
        <div data-id="element-8051">
          {hasMultipleRecipients ? <button className="px-5 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors flex items-center text-sm font-medium" onClick={handleSend} data-id="element-8052">
              <SendIcon size={16} className="mr-2" data-id="element-8053" />
              {t('components.renderFooter.send_to_all_recipients')}
            </button> : <>
              {(selectedOption === 'email' || selectedOption === 'sms') && <button className="px-5 py-2.5 bg-#E2231A text-white rounded-lg hover:bg-#EB4A42 transition-colors flex items-center text-sm font-medium disabled:opacity-50" onClick={handleSend} disabled={!recipientInput} data-id="element-8054">
                  <SendIcon size={16} className="mr-2" data-id="element-8055" />
                  {t('components.renderFooter.send')}
                </button>}
              {(selectedOption === 'copy' || selectedOption === 'qrcode') && <button className="px-5 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors text-sm font-medium" onClick={onClose} data-id="element-8056">
                  {t('components.renderFooter.done')}
                </button>}
              {!selectedOption && <button className="px-5 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed" disabled data-id="element-8057">
                  {t('components.renderFooter.select_an_option')}
                </button>}
            </>}
        </div>
      </div>;
  };
  return createPortal(<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-id="element-8058">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-8059">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-8060">
          <div className="flex items-center gap-3" data-id="element-8061">
            {step === 'preview' && splitType === 'dates' && <div className="w-8 h-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center" data-id="element-8062">
                <CalendarIcon size={16} className="text-[#9F140F]" data-id="element-8063" />
              </div>}
            {step === 'preview' && splitType === 'commission' && <div className="w-8 h-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center" data-id="element-8064">
                <PercentIcon size={16} className="text-[#9F140F]" data-id="element-8065" />
              </div>}
            <div data-id="element-8066">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-8067">
                {getModalTitle()}
              </h3>
              {step === 'preview' && <div className="flex items-center gap-2 mt-1" data-id="element-8068">
                  <div className="flex items-center gap-1" data-id="element-8069">
                    <div className="w-5 h-5 rounded-full bg-[#E2231A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-8070">
                      1
                    </div>
                    <span className="text-xs font-medium text-[#9F140F]" data-id="element-8071">
                      {t('components.ShareLinkModal.review')}
                    </span>
                  </div>
                  <div className="w-4 h-px bg-gray-300" data-id="element-8072" />
                  <div className="flex items-center gap-1" data-id="element-8073">
                    <div className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-bold" data-id="element-8074">
                      2
                    </div>
                    <span className="text-xs text-gray-400" data-id="element-8075">
                      {t('components.ShareLinkModal.share')}
                    </span>
                  </div>
                </div>}
              {step === 'share' && (splitType === 'dates' || splitType === 'commission') && <div className="flex items-center gap-2 mt-1" data-id="element-8076">
                    <div className="flex items-center gap-1" data-id="element-8077">
                      <div className="w-5 h-5 rounded-full bg-[#E2231A] text-white flex items-center justify-center" data-id="element-8078">
                        <CheckIcon size={10} data-id="element-8079" />
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-8080">
                        {t('components.ShareLinkModal.review')}
                      </span>
                    </div>
                    <div className="w-4 h-px bg-[#E2231A]" data-id="element-8081" />
                    <div className="flex items-center gap-1" data-id="element-8082">
                      <div className="w-5 h-5 rounded-full bg-[#E2231A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-8083">
                        2
                      </div>
                      <span className="text-xs font-medium text-[#9F140F]" data-id="element-8084">
                        {t('components.ShareLinkModal.share')}
                      </span>
                    </div>
                  </div>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-8085">
            <XIcon size={20} aria-hidden="true" data-id="element-8086" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[65vh] overflow-y-auto" data-id="element-8087">
          {step === 'preview' && splitType === 'dates' && renderSchedulePreview()}
          {step === 'preview' && splitType === 'commission' && renderCommissionPreview()}
          {step === 'share' && hasMultipleRecipients && renderMultiRecipientShare()}
          {step === 'share' && !hasMultipleRecipients && renderStandardShare()}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>, document.body);
}